import * as Yup from 'yup';
import { getIn, validateYupSchema, yupToFormErrors } from 'formik';
import { FarmRiskType, StructureCategory, policyType } from '@ourbranch/lookups';
import {
  hasInlandMarineSppItems,
  hasRateableScheduledFarmPropertyItems,
  isFarmBasedPolicyType,
  isLiabilityOnlyRiskSelection
} from '@ourbranch/farm-utils';
import { affinityToStem, getStateForcedAffinityStem } from '@ourbranch/policy-utils';
import { appConfig } from 'brand-config';
import { parseCustomAttribute } from 'core/helpers/parse-custom-attribute';

/**
 * Helper function to get building class 1 maximum age from brand configuration
 * @param {string} state - State code (e.g., 'IL', 'MO')
 * @returns {number} Maximum age in years for Building Class 1 (defaults to 25)
 */
const getBuildingClass1MaxAge = (state = 'IL') => {
  return appConfig?.farm?.[state]?.risks[FarmRiskType.Fo6Structures]?.businessRules?.buildingClass1MaxAgeYears ?? 25;
};

import {
  validUSState,
  requiredString,
  validHomePurchaseDate,
  requiredIfSectionTownshipRange,
  requiredIfLotBlock,
  isCountyRequired,
  requiredIfStreetAddress,
  requiredIfAreasUnderConstruction,
  requiredIfFO6BarnsOutbuildings,
  requiredIfFO6BarnsOutbuildingsAndHeatingEnabled,
  requiredForTowers,
  requiredForGrainStorage,
  requiredForNonTowerSilos,
  requiredIfFO6SilosGrainBins,
  requiredIfFO6SilosGrainBinsNonTower,
  requiredIfFO6SilosGrainBinsAndHeatingEnabled,
  requiredIfFO6Poultry,
  requiredIfFO6PoultryAndHeatingEnabled,
  requiredIfFO6ConfinementBuildings,
  requiredIfFO6HoopBuildings,
  requiredForRisks,
  validYearBuilt,
  mobileHomeSqFtValidation,
  nullifyIfNaN,
  findLocationStateInAncestors
} from 'common/helpers/yup-helpers';
import { getConstructionTypeExemptPropertyTypes, isFarmPolicyTypeLive } from 'common/helpers/farm-helpers';
import getErrorData from 'common/constants/get-error-data';
import { isValidVin } from 'core/helpers/vin-validator';
import { driversLicenseNumberSchema } from 'core/helpers/drivers-license-number-validations';

Yup.addMethod(Yup.string, 'requiredString', requiredString);
Yup.addMethod(Yup.mixed, 'validUSState', validUSState);
Yup.addMethod(Yup.mixed, 'validHomePurchaseDate', validHomePurchaseDate);
Yup.addMethod(Yup.string, 'requiredIfSectionTownshipRange', requiredIfSectionTownshipRange);
Yup.addMethod(Yup.string, 'requiredIfLotBlock', requiredIfLotBlock);
Yup.addMethod(Yup.string, 'isCountyRequired', isCountyRequired);
Yup.addMethod(Yup.string, 'requiredIfStreetAddress', requiredIfStreetAddress);
Yup.addMethod(Yup.string, 'requiredIfAreasUnderConstruction', requiredIfAreasUnderConstruction);
Yup.addMethod(Yup.string, 'requiredIfFO6BarnsOutbuildings', requiredIfFO6BarnsOutbuildings);
Yup.addMethod(Yup.number, 'requiredIfFO6BarnsOutbuildings', requiredIfFO6BarnsOutbuildings);
Yup.addMethod(
  Yup.string,
  'requiredIfFO6BarnsOutbuildingsAndHeatingEnabled',
  requiredIfFO6BarnsOutbuildingsAndHeatingEnabled
);
Yup.addMethod(Yup.string, 'requiredForTowers', requiredForTowers);
Yup.addMethod(Yup.string, 'requiredForGrainStorage', requiredForGrainStorage);
Yup.addMethod(Yup.string, 'requiredForNonTowerSilos', requiredForNonTowerSilos);
Yup.addMethod(Yup.string, 'requiredIfFO6SilosGrainBins', requiredIfFO6SilosGrainBins);
Yup.addMethod(Yup.number, 'requiredIfFO6SilosGrainBinsNonTower', requiredIfFO6SilosGrainBinsNonTower);
Yup.addMethod(Yup.string, 'requiredIfFO6SilosGrainBinsAndHeatingEnabled', requiredIfFO6SilosGrainBinsAndHeatingEnabled);
Yup.addMethod(Yup.string, 'requiredIfFO6Poultry', requiredIfFO6Poultry);
Yup.addMethod(Yup.number, 'requiredIfFO6Poultry', requiredIfFO6Poultry);
Yup.addMethod(Yup.string, 'requiredIfFO6PoultryAndHeatingEnabled', requiredIfFO6PoultryAndHeatingEnabled);
Yup.addMethod(Yup.string, 'requiredIfFO6ConfinementBuildings', requiredIfFO6ConfinementBuildings);
Yup.addMethod(Yup.string, 'requiredIfFO6HoopBuildings', requiredIfFO6HoopBuildings);
Yup.addMethod(Yup.number, 'requiredIfFO6ConfinementBuildings', requiredIfFO6ConfinementBuildings);
Yup.addMethod(Yup.number, 'requiredIfFO6HoopBuildings', requiredIfFO6HoopBuildings);
Yup.addMethod(Yup.mixed, 'requiredForRisks', requiredForRisks);
Yup.addMethod(Yup.string, 'validYearBuilt', validYearBuilt);
Yup.addMethod(Yup.mixed, 'mobileHomeSqFtValidation', mobileHomeSqFtValidation);
Yup.addMethod(Yup.number, 'nullifyIfNaN', nullifyIfNaN);

const phoneRequired = appConfig?.featureFlags?.phoneRequired ?? false;
const applicantDateOfBirthAtNewOffer = appConfig?.featureFlags?.applicantDateOfBirthAtNewOffer ?? false;

export const priorAddressSchema = Yup.object().shape({
  priorAddress: Yup.object().shape({
    address: Yup.string().requiredString('Address is required'),
    state: Yup.string().requiredString('State is Required').validUSState('priorAddress.state'),
    zip: Yup.string().requiredString('Zip is required'),
    city: Yup.string().requiredString('City is required')
  })
});

export const priorCoverageSchema = Yup.object().shape({
  priorCoverageInYears: Yup.string().required('Prior coverage in years is required'),
  policyLimitBIPD: Yup.string().required('Policy limit BIPD is required')
});

export const driverInformationSchema = Yup.object().shape({
  driversLicenseNumber: Yup.string().required('Driver license number is required'),
  driversLicenseState: Yup.string().required('Driver license state is required')
});

export const vinSchema = Yup.object().shape({
  VIN: Yup.string()
    .ensure()
    .test('is-valid-vin', 'Please enter a valid car VIN', (val) => isValidVin(val))
});

export const homeSchema = Yup.object().shape({
  home: Yup.object().shape({
    basementSqFt: Yup.string().requiredString('Square footage of basement is required'),
    basementType: Yup.string().requiredString('Basement type is required'),
    constructionType: Yup.string().requiredString('Construction type is required'),
    exteriorWallType: Yup.string().requiredString('Exterior wall type is required'),
    garageCarCapacity: Yup.string().requiredString('Garage car capacity is required'),
    garageType: Yup.string().requiredString('Garage type is required'),
    homeQuality: Yup.string().requiredString('Home quality is required'),
    numFamiliesInDwelling: Yup.string().requiredString('Number of families in the dwelling is required'),
    numFireplaces: Yup.string().requiredString('Number of fireplaces is required'),
    numFullBathrooms: Yup.string().requiredString('Number of full bathrooms is required'),
    numHalfBathrooms: Yup.string().requiredString('Number of half bathrooms is required'),
    numStories: Yup.string().requiredString('Number of stories is required'),
    numWoodBurningStoves: Yup.string().requiredString('Number of wood burning stoves is required'),
    purchaseDate: Yup.string().validHomePurchaseDate('home.purchaseDate'),
    roofShape: Yup.string().requiredString('Roof shape is required'),
    roofType: Yup.string().requiredString('Roof type is required'),
    roofYear: Yup.string().requiredString('Roof year is required'),
    sqFt: Yup.string().requiredString('Square footage is required'),
    typeOfHome: Yup.string().requiredString('Type of home is required'),
    yearBuilt: Yup.string().requiredString('Build year is required'),
    longitude: Yup.number().nullifyIfNaN().negative('Longitude must be a negative number for USA')
  })
});

// Condo counterpart of homeSchema: required for new-construction condos and as the 5004/50006
// alert schema for condo policy types.
export const condoSchema = Yup.object().shape({
  condo: Yup.object().shape({
    constructionType: Yup.string().requiredString('Construction type is required'),
    condoQuality: Yup.string().requiredString('Condo quality is required'),
    numFullBathrooms: Yup.string().requiredString('Number of full bathrooms is required'),
    numHalfBathrooms: Yup.string().requiredString('Number of half bathrooms is required'),
    purchaseDate: Yup.string().validHomePurchaseDate('condo.purchaseDate'),
    roofShape: Yup.string().requiredString('Roof shape is required'),
    sqFt: Yup.string().requiredString('Square footage is required'),
    typeOfCondo: Yup.string().requiredString('Type of condo is required'),
    yearBuilt: Yup.string().requiredString('Build year is required').validYearBuilt(),
    monthsRentedOut: Yup.number().required('Months rented out is required'),
    longitude: Yup.number().nullifyIfNaN().negative('Longitude must be a negative number for USA')
  })
});

// 5023: geocoding couldn't place the address; the rater needs explicit coordinates.
export const homeCoordinatesSchema = Yup.object().shape({
  home: Yup.object().shape({
    latitude: Yup.number().nullifyIfNaN().nullable().required('Latitude is required'),
    longitude: Yup.number()
      .nullifyIfNaN()
      .nullable()
      .required('Longitude is required')
      .negative('Longitude must be a negative number for USA')
  })
});

export const unitSchema = Yup.object().shape({
  unit: Yup.string()
    .nullable()
    .test('required-unless-no-unit-attested', 'Unit number is required', function (value) {
      if (this.options.context?.noUnitNumber) {
        return true;
      }
      return !!value && !!String(value).trim();
    })
});

export const dateOfBirthSchema = Yup.object().shape({
  dateOfBirth: Yup.string()
    .nullable()
    .required('Date of birth is required')
    .test('is-valid-date', 'Date of birth must be a valid date', (val) => {
      if (!val) {
        return true; // let required() handle empty
      }
      const d = new Date(val);
      return !isNaN(d.getTime());
    })
    .test('is-past-date', 'Date of birth must be in the past', (val) => {
      if (!val) {
        return true;
      }
      const d = new Date(val);
      return !isNaN(d.getTime()) && d < new Date();
    })
});

const shouldValidate = (values, targetKeys, prefix = '') => {
  if (!targetKeys || !targetKeys.length) {
    return false;
  }
  return targetKeys.find((key) => getIn(values, `${prefix}${key}`));
};

// Exported for the schema-path guard test, which asserts every validated path exists on the
// form (a typo'd path silently passes client validation and turns into a server round trip).
export const validationSchema = (_errorCodes, values, allStates, isAgency, alerts, user = {}) => {
  const hasCondo = [policyType.Condo, policyType.CABundle].includes(values.policyType);
  // not including HomeBundle because we dont do that at the new quote page
  const hasHome = [policyType.Home, policyType.HABundle].includes(values.policyType);
  const hasHomeOrCondo = hasHome || hasCondo;
  const hasFarm = isFarmBasedPolicyType(values.policyType);

  const base = Yup.object().shape({
    firstName: Yup.string().requiredString('First name is required'),
    lastName: Yup.string().requiredString('Last name is required'),
    address: Yup.string().requiredString('Address is required'),
    state: Yup.string()
      .requiredString('State is required')
      .validUSState('state')
      .test(
        'is-licensed-in-state',
        'You do not have this state in your allowed states list nor your unlicensed states list on your user profile. If you believe this is a mistake, please reach out to Support.',
        (val) => {
          // Brands like Forreston/Oakwood/CFM relax this check on the named insured because
          // a farm agent's personal mailing address may be in a state they don't write in;
          // the licensed-state check is enforced per farm location instead. (Issue #30876)
          if (appConfig?.featureFlags?.enforceLicensedStateOnNamedInsured === false) {
            return true;
          }
          return allStates.includes(val);
        }
      ),
    zip: Yup.string().requiredString('Zip is required'),
    city: Yup.string().requiredString('City is required'),
    unit: Yup.string()
      .nullable()
      .test('required-in-condo', `Condo's apartment or unit number is required`, (value) => {
        if (hasCondo && !values.noUnitNumber) {
          return !!value;
        }
        return true;
      }),
    leadSource: Yup.string().nullable(),
    fcraDisclaimer: Yup.boolean().oneOf([true], 'Must agree to FCRA disclaimer'),
    driversLicenseNumber: hasFarm ? Yup.string().nullable() : driversLicenseNumberSchema.nullable(),
    driversLicenseState: hasFarm
      ? Yup.string().nullable()
      : Yup.string().nullable().validUSState('driversLicenseState'),
    affinity: Yup.string()
      .nullable()
      .test('RequiredForAgency', 'Affinity is required', (val) => {
        return !isAgency || !!val;
      })
      .test('state-forced-affinity-stem', '', function (val) {
        const forcedStem = val && getStateForcedAffinityStem({ state: values.state, affinity: val });
        if (!forcedStem) {
          return true;
        }
        const parsedStems = parseCustomAttribute(user.affinityStems);
        const hasCodeGrant = (user.viewableAffinityCodes ?? []).some(
          (code) => typeof code === 'string' && affinityToStem(code) === forcedStem
        );
        const hasStemAttributeGrant = (Array.isArray(parsedStems) ? parsedStems : []).some((entry) => {
          if (typeof entry !== 'string') {
            return false;
          }
          if (affinityToStem(entry) === forcedStem) {
            return true;
          }
          try {
            return new RegExp(entry).test(`${forcedStem}_0`);
          } catch {
            return false;
          }
        });
        const hasStemGrant = hasCodeGrant || hasStemAttributeGrant;
        return this.createError({
          message: hasStemGrant
            ? `Kentucky business must be written under the Kentucky field agent's ${forcedStem} code - pick it from the Producer Code list.`
            : `Kentucky business must be written under a Kentucky field agent's ${forcedStem} code. Your profile doesn't have one - contact support to update your configuration.`
        });
      }),
    phone: Yup.string()
      .nullable()
      .test('phone-validation', function (value) {
        if (!value || value === '') {
          if (phoneRequired) {
            return this.createError({ message: 'Phone number is required' });
          }
          return true;
        }
        if (value.length < 10) {
          return this.createError({ message: 'Must be at least 10 digits' });
        }
        return true;
      }),
    policyType: Yup.string().requiredString('Policy type is required').nullable(),
    additionalPhoneNumbers: Yup.array()
      .of(
        Yup.object().shape({
          phoneNumber: Yup.string()
            .required('Required or click trash to remove')
            .min(10, 'Must be at least 10 digits')
            .test(
              'check-for-primary-phone',
              'You cannot add a secondary phone without a primary phone',
              (secondaryPhone) => {
                return (secondaryPhone && secondaryPhone.length > 0 && values.phone?.length > 0) || !secondaryPhone;
              }
            ),
          note: Yup.string().max(50, 'Must be less than 50 characters').nullable()
        })
      )
      .nullable(),
    home: Yup.object().shape({
      longitude: Yup.number().nullifyIfNaN().negative('Longitude must be a negative number for USA')
    }),
    entity: Yup.object().shape({
      id: Yup.string().nullable(),
      type: Yup.string()
        .nullable()
        .test('required-for-farm', 'Entity type is required', (value) => {
          if (hasFarm) {
            return !!value;
          }
          return true;
        }),
      name: Yup.string()
        .nullable()
        .test('required-for-non-individual-entity', 'Entity name is required', function (value) {
          const entityType = this.parent.type;
          if (entityType && entityType !== 'Individual' && entityType !== 'Joint') {
            return !!value;
          }
          return true;
        }),
      address: Yup.object()
        .shape({
          address: Yup.string().nullable(),
          address2: Yup.string().nullable(),
          city: Yup.string().nullable(),
          state: Yup.string().nullable(),
          zip: Yup.string().nullable()
        })
        .nullable()
    })
  });
  let final = base;

  // if any values in the priorAddress section are filled in, validate for all values in that section
  const priorAddress = shouldValidate(values, Object.keys(values.priorAddress || []), 'priorAddress.');
  const addingCar = values.VIN;
  const isNewConstruction = values.isNewConstruction;
  const isNewPurchase = values.isNewPurchase;

  if (addingCar) {
    final = final.concat(vinSchema);
  }

  if (priorAddress) {
    final = final.concat(priorAddressSchema);
  }

  if (hasFarm) {
    final = final.concat(
      Yup.object().shape({
        farmLocations: Yup.array().of(
          Yup.object().shape({
            description: Yup.string().requiredString('Location Name is required'),
            numberOfAcres: Yup.string().requiredString('Acres is required'),
            legalDescription: Yup.object().shape({
              lot: Yup.string().requiredIfLotBlock('Lot is required'),
              block: Yup.string().requiredIfLotBlock('Block is required'),
              section: Yup.string().requiredIfSectionTownshipRange('Section is required'),
              township: Yup.string().requiredIfSectionTownshipRange('Township is required'),
              range: Yup.string().requiredIfSectionTownshipRange('Range is required'),
              townshipCoordinates: Yup.string(),
              rangeCoordinates: Yup.string(),
              format: Yup.string().requiredString('Legal Description Format is required')
            }),
            address: Yup.object().shape({
              address: Yup.string().requiredIfStreetAddress('Address is required'),
              address2: Yup.string().nullable(),
              city: Yup.string().requiredString('City is required'),
              state: Yup.string()
                .requiredString('State is required')
                .test('farm-location-state-eligible', '', function (val) {
                  // #32037: Home (Z) / Rental (Y) are state-restricted (Forreston: IL only).
                  // Enforce eligibility against the farm LOCATION (risk) address state, not the
                  // named insured's mailing-address state — the property may be in IL while the
                  // insured's mailing address is elsewhere (e.g. an out-of-state landlord). Full
                  // Farm (F) and non-farm types are not gated here.
                  const selectedPolicyType = values.policyType;
                  if (selectedPolicyType !== policyType.FarmHome && selectedPolicyType !== policyType.FarmRental) {
                    return true;
                  }
                  if (!val || isFarmPolicyTypeLive(selectedPolicyType, val)) {
                    return true;
                  }
                  const eligibleStates = Object.keys(appConfig?.farm ?? {}).filter(
                    (s) => appConfig.farm[s]?.policyTypeMasks?.[selectedPolicyType]
                  );
                  return this.createError({
                    message: `This policy type is only available for properties located in: ${
                      eligibleStates.join(', ') || 'no states'
                    } (this location is in ${val}).`
                  });
                }),
              zip: Yup.string().requiredString('ZIP is required'),
              county: Yup.string().isCountyRequired()
            }),
            risks: Yup.array().of(
              Yup.object().shape({
                type: Yup.string()
                  .requiredString('Risk Type is required')
                  // #33164: a standalone inland marine risk IS its SPP items — it has no building and
                  // no coverage fields of its own. A farm offer's total premium can never be $0, so a
                  // new offer whose only risk has no items fails outright at ClarionDoor ("did not
                  // receive premium") rather than returning a cheap quote. Requiring the first item
                  // before submit is what keeps the agent from creating an offer that cannot rate.
                  //
                  // The error is raised on `type` rather than the items path deliberately: this select
                  // is the field the agent just used and sits directly above the items section, so the
                  // message is visible where they are looking. An error on
                  // `coverages.scheduleCoverages` has no field rendering it in the quote form.
                  .test('inland-marine-requires-item', 'Add at least one inland marine item', function (value) {
                    if (value !== FarmRiskType.InlandMarine) {
                      return true;
                    }
                    return hasInlandMarineSppItems(this.parent);
                  })
                  // #34585: FO-6 scheduled farm property is the same shape as inland marine above —
                  // its Coverage F items ARE the risk's entire premium, so a new offer whose only
                  // risk has none rates at $0 and comes back as BLOCK 5042 "Farm rating error" on
                  // the very first "See price". The check is for a *rateable* item (positive limit),
                  // not a non-empty list: a category picked with no limit rates identically to no
                  // item at all. Error on `type` for the same reason as inland marine — nothing in
                  // this form renders an error at `coverages.coverageF`.
                  .test(
                    'fo6-scheduled-requires-item',
                    'Add at least one scheduled farm property item',
                    function (value) {
                      if (value !== FarmRiskType.Fo6Scheduled) {
                        return true;
                      }
                      return hasRateableScheduledFarmPropertyItems(this.parent);
                    }
                  )
                  // #34585: `description` is non-null on ScheduledFarmPropertyItemDetailsInput, so a
                  // blank one fails the requestQuote mutation outright — an even more opaque failure
                  // than the rating block. (The other two non-null fields are covered: `limit` by the
                  // rule above, `deductible` by the form's default.)
                  .test(
                    'fo6-scheduled-item-description',
                    'Enter a description for the scheduled farm property item',
                    function (value) {
                      if (value !== FarmRiskType.Fo6Scheduled) {
                        return true;
                      }
                      const items = this.parent?.coverages?.coverageF;
                      if (!Array.isArray(items)) {
                        return true; // the requires-item rule above already reports this
                      }
                      return items.every((item) => !item || String(item.description ?? '').trim().length > 0);
                    }
                  ),
                description: Yup.string().test('required-unless-fo6', 'Risk Name is required', function (value) {
                  const farmRiskType = getIn(this.parent, 'type');
                  if (
                    farmRiskType === FarmRiskType.Fo6Scheduled ||
                    farmRiskType === FarmRiskType.Fo6Unscheduled ||
                    farmRiskType === FarmRiskType.InlandMarine ||
                    isLiabilityOnlyRiskSelection(farmRiskType)
                  ) {
                    // Auto-named types — the agent never types a name (#33164 inland marine, #33168 liability)
                    return true;
                  }
                  return !!value; // Required for other risk types
                }),
                building: Yup.object().shape({
                  purchaseDate: Yup.string().nullable(),
                  typeOfHome: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Home type is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  ownershipStatus: Yup.string().nullable(),
                  numFamiliesInDwelling: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Families in dwelling is required',
                      risks: [
                        { risk: FarmRiskType.Fo1, requiredForPropertyTypes: ['MH'] },
                        { risk: FarmRiskType.Fo2, requiredForPropertyTypes: ['MH'] },
                        { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B', 'MH', 'MD'] },
                        { risk: FarmRiskType.Fo4 }
                      ]
                    }),
                  homeQuality: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Home quality is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  mobileHomeQualityGrade: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Home quality is required',
                      risks: [
                        { risk: FarmRiskType.Fo1, requiredForPropertyTypes: ['MH'] },
                        { risk: FarmRiskType.Fo2, requiredForPropertyTypes: ['MH'] },
                        { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] }
                      ]
                    }),
                  mobileHomeConfig: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Mobile home config is required',
                      risks: [
                        { risk: FarmRiskType.Fo1, requiredForPropertyTypes: ['MH'] },
                        { risk: FarmRiskType.Fo2, requiredForPropertyTypes: ['MH'] },
                        { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] }
                      ]
                    }),
                  diameter: Yup.string().nullable().requiredForTowers('Diameter is required'),
                  height: Yup.string()
                    .nullable()
                    .test('required-sg-height', 'Height is required', function (value) {
                      const buildingType = getIn(this.parent, 'buildingType');
                      const occupancy = getIn(this.parent, 'occupancy');
                      // Height is hidden for Bulk Bin/Hopper (GHH) and length/width types (CC, GS, GH)
                      const hiddenHeightOccupancies = ['GHH', 'CC', 'GS', 'GH'];
                      if (buildingType === 'SG' && occupancy && !hiddenHeightOccupancies.includes(occupancy)) {
                        return !!value;
                      }
                      return true;
                    }),
                  bushelCapacity: Yup.string().nullable().requiredForGrainStorage('Bushel capacity is required'),
                  numStories: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Stories is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  numHalfBathrooms: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Half bathrooms is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  numFullBathrooms: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Full bathrooms is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  numFireplaces: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Fireplaces is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  numWoodBurningStoves: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Wood stoves is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  basementType: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Basement/Foundation is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  basementSqFt: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Basement sq ft is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  exteriorWallType: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Exterior wall finish is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  garageType: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Garage type is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  garageCarCapacity: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: "Garage's car space is required",
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  roofOriginal: Yup.string().nullable(),
                  roofYear: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Roof year is required',
                      risks: [
                        { risk: FarmRiskType.Fo2, requiredForPropertyTypes: ['B', 'MH'] },
                        { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B', 'MH'] }
                      ]
                    })
                    .requiredIfFO6BarnsOutbuildings('Roof year is required')
                    .requiredIfFO6ConfinementBuildings('Roof year is required')
                    .requiredIfFO6HoopBuildings('Roof year is required')
                    .requiredIfFO6Poultry('Roof year is required'),
                  roofType: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Roof type is required',
                      risks: [
                        { risk: FarmRiskType.Fo2, requiredForPropertyTypes: ['B', 'MH'] },
                        { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B', 'MH'] }
                      ]
                    })
                    .requiredIfFO6BarnsOutbuildings('Roof type is required')
                    .requiredIfFO6ConfinementBuildings('Roof type is required')
                    .requiredIfFO6Poultry('Roof type is required'),
                  // #37180: an FO-6 structure's roof shape is the 360Value rooftype; roofType (above) is its material.
                  roofShape: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Roof shape is required',
                      risks: [
                        { risk: FarmRiskType.Fo2, requiredForPropertyTypes: ['B', 'MH'] },
                        { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B', 'MH'] }
                      ]
                    })
                    .requiredIfFO6BarnsOutbuildings('Roof shape is required')
                    .requiredIfFO6ConfinementBuildings('Roof shape is required')
                    .requiredIfFO6Poultry('Roof shape is required'),
                  occupancy: Yup.string()
                    .nullable()
                    .requiredIfFO6BarnsOutbuildings('Occupancy is required')
                    .requiredIfFO6SilosGrainBins('Occupancy is required')
                    .requiredIfFO6ConfinementBuildings('Occupancy is required'),
                  yearBuilt: Yup.string()
                    .nullable()
                    .validYearBuilt()
                    .requiredForRisks({
                      message: 'Year built is required',
                      risks: [
                        { risk: FarmRiskType.Fo2, requiredForPropertyTypes: ['B', 'MH'] },
                        { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B', 'MH'] }
                      ]
                    })
                    .requiredIfFO6BarnsOutbuildings('Year built is required')
                    .requiredIfFO6ConfinementBuildings('Year built is required')
                    .requiredIfFO6HoopBuildings('Year built is required')
                    .requiredIfFO6Poultry('Year built is required')
                    .requiredIfFO6SilosGrainBins('Year built is required'),
                  sqFt: Yup.number()
                    .nullable()
                    .requiredForRisks({
                      message: 'Above-ground sq ft is required',
                      risks: [
                        { risk: FarmRiskType.Fo2, requiredForPropertyTypes: ['B', 'MH'] },
                        { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B', 'MH'] }
                      ]
                    })
                    .requiredIfFO6BarnsOutbuildings('Square footage is required')
                    .requiredIfFO6ConfinementBuildings('Above-ground sq ft is required')
                    .requiredIfFO6HoopBuildings('Above-ground sq ft is required')
                    .requiredIfFO6Poultry('Square footage is required')
                    .requiredIfFO6SilosGrainBinsNonTower('Square footage is required')
                    .min(150, 'Square footage must be greater than 150 square feet')
                    .mobileHomeSqFtValidation(),
                  buildingQuality: Yup.string()
                    .nullable()
                    .requiredIfFO6BarnsOutbuildings('Quality is required')
                    .requiredIfFO6SilosGrainBins('Quality is required')
                    .requiredIfFO6ConfinementBuildings('Quality is required')
                    .requiredIfFO6HoopBuildings('Quality is required')
                    .requiredIfFO6Poultry('Quality is required'),
                  buildingClass: Yup.number()
                    .nullable()
                    .requiredIfFO6BarnsOutbuildings('Building class is required')
                    .test(
                      'building-class-1-age-eligibility',
                      function () {
                        // Dynamic error message with configurable age limit
                        const state = findLocationStateInAncestors(this.from);
                        const maxAge = getBuildingClass1MaxAge(state);
                        return `Building Class 1 requires building age ≤ ${maxAge} years and no open sides`;
                      },
                      function (value) {
                        // Only validate for FO6 Barns/Outbuildings with Building Class 1
                        const buildingType = getIn(this.parent, 'buildingType');
                        const riskType = getIn(this.from[1].value, 'type');

                        if (
                          riskType !== FarmRiskType.Fo6Structures ||
                          buildingType !== StructureCategory.BarnsOutbuildings ||
                          value !== 1
                        ) {
                          return true;
                        }

                        const yearBuilt = getIn(this.parent, 'yearBuilt');
                        const hasOpenSides = getIn(this.parent, 'structureOpenSides');

                        const currentYear = new Date().getFullYear();
                        const buildingAge = yearBuilt ? currentYear - parseInt(yearBuilt, 10) : null;

                        // State lives on the farm LOCATION ancestor, not the risk — this.from[1]
                        // is the risk node, which has no address, so the old lookup always fell
                        // back to the default max age instead of the state's configured value.
                        const state = findLocationStateInAncestors(this.from);
                        const maxAge = getBuildingClass1MaxAge(state);

                        // Class 1 requires age ≤ maxAge years AND no open sides
                        const errors = [];

                        if (buildingAge !== null && buildingAge > maxAge) {
                          errors.push(`building age ≤ ${maxAge} years (current: ${buildingAge} years)`);
                        }

                        if (hasOpenSides) {
                          errors.push('no open sides allowed');
                        }

                        if (errors.length > 0) {
                          return this.createError({
                            message: `Building Class 1 requires: ${errors.join(', ')}.`
                          });
                        }

                        return true;
                      }
                    ),
                  primaryHeatingSource: Yup.string()
                    .nullable()
                    .requiredIfFO6BarnsOutbuildings('Primary heating is required')
                    .requiredIfFO6Poultry('Primary heating is required')
                    .requiredIfFO6ConfinementBuildings('Primary heating is required')
                    .requiredIfFO6HoopBuildings('Primary heating is required')
                    .requiredForRisks({
                      message: 'Primary heating is required',
                      risks: [
                        { risk: FarmRiskType.Fo2, requiredForPropertyTypes: ['MH'] },
                        { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B', 'MH'] }
                      ]
                    }),
                  numHeatingSources: Yup.string().nullable(),
                  constructionType: Yup.string()
                    .requiredForRisks({
                      message: 'Construction type is required',
                      risks: [{ risk: FarmRiskType.Fo4 }]
                    })
                    .requiredIfFO6BarnsOutbuildings('Construction type is required')
                    .requiredIfFO6SilosGrainBins('Construction type is required')
                    .requiredIfFO6Poultry('Construction type is required')
                    .requiredIfFO6ConfinementBuildings('Construction type is required')
                    .requiredIfFO6HoopBuildings('Construction type is required')
                    .requiredForRisks({
                      message: 'Construction type is required',
                      risks: [
                        {
                          risk: FarmRiskType.Fo3,
                          requiredForPropertyTypes: ['B', 'MH'],
                          excludedPropertyTypes: ({ state }) =>
                            getConstructionTypeExemptPropertyTypes(state, FarmRiskType.Fo3)
                        }
                      ]
                    }),
                  numSolidFuelBurningAppliances: Yup.string()
                    .requiredForRisks({
                      message: 'Solid fuel burning appliances number is required',
                      risks: [{ risk: FarmRiskType.Fo4 }]
                    })
                    .requiredIfFO6BarnsOutbuildings('Solid fuel burning appliances is required')
                    .requiredIfFO6Poultry('Solid fuel burning appliances is required')
                    .requiredIfFO6ConfinementBuildings('Solid fuel burning appliances is required')
                    .requiredIfFO6HoopBuildings('Solid fuel burning appliances is required')
                    .requiredForRisks({
                      message:
                        'Num solid fuel burning appliances in dwelling or related private structures is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B', 'MH'] }]
                    }),
                  constructionStyle: Yup.string().nullable(),
                  propertyType: Yup.string().requiredForRisks({
                    message: 'Structure category is required',
                    risks: [{ risk: FarmRiskType.Fo3 }, { risk: FarmRiskType.Fo4 }]
                  }),
                  residenceUsage: Yup.string().requiredForRisks({
                    message: 'Residence type/usage is required',
                    risks: [{ risk: FarmRiskType.Fo3 }, { risk: FarmRiskType.Fo4 }]
                  }),
                  // Mobile home specific fields
                  tieDownType: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Tie down type is required',
                      risks: [
                        { risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['MH'] },
                        { risk: FarmRiskType.Fo4, requiredForPropertyTypes: ['MH'] }
                      ]
                    }),
                  enclosedMasonryFoundation: Yup.boolean().nullable(),
                  fullSkirtingAroundBase: Yup.boolean().nullable(),
                  // Fields for both Barndominium and MobileHome
                  renovation: Yup.object().shape({
                    electricalType: Yup.string().requiredIfAreasUnderConstruction(
                      'Electric Type is required when renovations are selected'
                    ),
                    electricalService: Yup.string().requiredIfAreasUnderConstruction(
                      'Electrical Service Update is required when renovations are selected'
                    ),
                    heatingType: Yup.string().requiredIfAreasUnderConstruction(
                      'Heating Type is required when renovations are selected'
                    ),
                    heatingYear: Yup.string().requiredIfAreasUnderConstruction(
                      'Heating Year is required when renovations are selected'
                    ),
                    coolingYear: Yup.string().requiredIfAreasUnderConstruction(
                      'AC Year is required when renovations are selected'
                    ),
                    plumbingYear: Yup.string().requiredIfAreasUnderConstruction(
                      'Plumbing Year is required when renovations are selected'
                    ),
                    // Barndominium specific fields (also conditional on areasUnderConstruction)
                    electricalYear: Yup.string().requiredIfAreasUnderConstruction(
                      'Electrical Year is required when renovations are selected'
                    )
                  }),
                  attachedStructureSqFt: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Attached garage sq ft is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B'] }]
                    }),
                  numSecondaryHeatingSources: Yup.string().nullable(),
                  secondaryHeatingSource: Yup.string()
                    .nullable()
                    .requiredForRisks({
                      message: 'Secondary heat is required',
                      risks: [{ risk: FarmRiskType.Fo3, requiredForPropertyTypes: ['B', 'MH'] }]
                    }),
                  latitude: Yup.string().nullable(),
                  longitude: Yup.string().nullable()
                }),
                connectedHome: Yup.object().shape({
                  theftPreventionDeviceType: Yup.mixed().nullable(),
                  fireProtectiveDeviceType: Yup.mixed().nullable(),
                  internetMonitoring: Yup.boolean().nullable()
                })
              })
            )
          })
        )
      })
    );
  }

  // DOB is required from the start (like first/last name) for brands that collect it at new-offer
  // time. Not gated on policy type: farm brands begin with an empty policyType, so gating on hasFarm
  // deferred the requirement until a policy type was chosen. See issue #32430.
  if (applicantDateOfBirthAtNewOffer) {
    final = final.concat(dateOfBirthSchema);
  }

  // validation required if house/condo is new construction
  if (isNewConstruction && hasHomeOrCondo) {
    final = final.concat(priorAddressSchema);
    if (hasHome) {
      final = final.concat(homeSchema);
    } else if (hasCondo) {
      final = final.concat(condoSchema);
    }
  }
  // validation required if house/condo is new Purchase
  if (isNewPurchase && hasHomeOrCondo) {
    const validationObj = {
      home: Yup.object().shape({
        purchaseDate: Yup.string().validHomePurchaseDate('home.purchaseDate', false)
      })
    };

    // Only validate condo.purchaseDate if policy includes condo
    if (hasCondo) {
      validationObj.condo = Yup.object().shape({
        purchaseDate: Yup.string().validHomePurchaseDate('condo.purchaseDate', false)
      });
    }

    final = final.concat(Yup.object().shape(validationObj)).concat(priorAddressSchema);
  }

  const alertSpecificValidationSchemas = alerts.map((alert) => {
    return getErrorData({ error: { code: alert.code }, policyType: values.policyType }).quoteFormAlertValidationSchema;
  });

  if (alertSpecificValidationSchemas && alertSpecificValidationSchemas.length > 0) {
    final = alertSpecificValidationSchemas.reduce((acc, schema) => (schema ? acc.concat(schema) : acc), final);
  }

  // Re-apply Farm-specific relaxations after alert schemas, since alert schemas (e.g. 5012's
  // driverInformationSchema) can override the nullable driversLicense fields set in the base schema
  if (hasFarm) {
    final = final.concat(
      Yup.object().shape({
        driversLicenseNumber: Yup.string().nullable(),
        driversLicenseState: Yup.string().nullable()
      })
    );
  }

  return final;
};

export const validateQuoteForm = (errorCodes, values, allStates, isAgency, alerts, user) => {
  return validateYupSchema(
    values,
    validationSchema(errorCodes, values, allStates, isAgency, alerts, user),
    false,
    values
  )
    .then(() => {
      return null;
    })
    .catch((e) => {
      if (e.name && e.name === 'ValidationError') {
        throw yupToFormErrors(e);
      }
      throw e;
    });
};
